"use client";

import { cn, Icon, Typography } from "@kyan/ui";
import React from "react";

import {
  type Strategy,
  type StrategyId,
  useStrategySelectorController,
} from "./StrategySelector.controller";

export interface StrategySelectorBaseProps {
  className?: string;
  strategy: StrategyId;
  strategies: Strategy[];
  isComboStatePrices: boolean;
  handleSelect: (id: StrategyId) => () => void;
}

export const StrategySelectorBase: React.FC<StrategySelectorBaseProps> = ({
  className,
  strategy,
  strategies,
  isComboStatePrices,
  handleSelect,
}) => {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <Typography grotesk semibold element="h3" id="strategy-selector-label">
        Choose a strategy
      </Typography>
      <div
        role="radiogroup"
        className="grid grid-cols-6 gap-2"
        aria-labelledby="strategy-selector-label"
      >
        {strategies.map(({ id, label }) => (
          <button
            key={id}
            disabled={isComboStatePrices}
            aria-pressed={strategy === id}
            className={cn(
              "bg-buttonBg dark:bg-buttonFill enabled:hover:bg-buttonHover border-divider flex h-15 cursor-pointer items-center justify-center gap-2 rounded-sm border p-2 transition-colors disabled:cursor-not-allowed dark:border-transparent",
              {
                "!border-primary !bg-primary/10 text-textPrimary":
                  strategy === id,
                "opacity-50": strategy !== id && isComboStatePrices,
              },
            )}
            onClick={handleSelect(id)}
          >
            {strategy === id && isComboStatePrices && (
              <Icon
                size="sm"
                name="lock"
                data-testid="icon"
                className="text-primary"
              />
            )}
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export interface StrategySelectorProps {
  className?: string;
}

export const StrategySelector: React.FC<StrategySelectorProps> = ({
  className,
}) => {
  const strategySelectorProps = useStrategySelectorController();

  return (
    <StrategySelectorBase className={className} {...strategySelectorProps} />
  );
};
